// UserProfile.jsx
import React, { useEffect, useState } from "react";
import { FaBell, FaBuilding, FaCamera, FaUserCog, FaBox, FaCalendarAlt, FaClock, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { motion } from "framer-motion";
import { useGetUserData } from "../../../hooks/useGetUserData";
import { useParams } from "react-router-dom";
import drEasyApi from "../../../Api/Customapi";
import { message } from "antd";
import { getImg } from "../../../Api/getImg";
import { checkUserStatus } from "../../../Api/userStatus";
import { LuLoader } from "react-icons/lu";
import { useLanguage } from "../../../context/LanguageContext";

const UserProfile = () => {
    const { t } = useLanguage();
    const [activeTab, setActiveTab] = useState("account");
    const { slug } = useParams();
    const { data: user, isLoading, error, refetch } = useGetUserData(slug);
    const [uploading, setUploading] = useState(false);
    const [status, setStatus] = useState(null);
    const [packages, setPackages] = useState([]);
    const [packagesLoading, setPackagesLoading] = useState(false);
    
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const result = await checkUserStatus(slug);
                setStatus(result); 
            } catch (err) {
                console.log(err);
            }
        };

        fetchStatus();
    }, [slug]);

    // Fetch packages when "packages" tab is active
    useEffect(() => {
        if (activeTab === "packages") {
            fetchPackages();
        }
    }, [activeTab]);

    const fetchPackages = async () => {
        try {
            setPackagesLoading(true);
            const response = await drEasyApi.get("/api/Packages/GetMyPackages");
            setPackages(response.data);
        } catch (err) {
            console.error(err);
            message.error(t('userProfile.packages.loadError'));
        } finally {
            setPackagesLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getDaysRemaining = (expiryDate) => {
        const today = new Date();
        const expiry = new Date(expiryDate);
        const diffTime = expiry - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(file.type)) {
            message.error(t('userProfile.imageUpload.invalidType'));
            return;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append("file", file);

            const { data: imageData } = await drEasyApi.post("/api/Storage/UploadImage", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            const imageId = imageData.id;
            await drEasyApi.post(`/api/${slug}/User/UploadImage`, { imageId });

            message.success(t('userProfile.imageUpload.success'));
            refetch();
        } catch (err) {
            console.error(err);
            message.error(t('userProfile.imageUpload.error'));
        } finally {
            setUploading(false);
        }
    };

    if (isLoading) return <div className="text-center mt-20"><LuLoader className="animate-spin"/></div>;
    if (error) return <div className="text-center mt-20 text-red-500">{t('userProfile.loadError')}</div>;

    return (
        <div className="relative min-h-screen pb-16 px-4">
            {/* Header Image */}
            <div className="w-full h-72 bg-[url('https://cdn.pixabay.com/photo/2022/08/10/18/57/lake-7377942_1280.jpg')] bg-cover bg-center rounded-md shadow-sm relative">
            </div>

            {/* Responsive Cards Wrapper */}
            <div className="relative flex flex-col lg:flex-row justify-between gap-6 px-2 sm:px-20 md:px-40 xl:px-60 mt-[-5rem] z-10">
                {/* Profile Card */}
                <motion.div
                    initial={{ opacity: 0, y: 100 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9 }}
                    className="bg-white p-6 rounded-xl shadow-md w-full lg:w-[36%] xl:w-[28%] mx-auto lg:mx-0"
                >
                    <div className="flex flex-col items-center text-center gap-3">
                        <div className="relative p-2 border border-primary rounded-full">
                            <img
                                className="w-36 h-36 rounded-full border-4 border-white object-cover"
                                src={getImg(user?.imageId) || "https://res.cloudinary.com/dk2uh7sho/image/upload/v1750278754/default-img_tbc2pe.jpg"}
                                alt="Avatar"
                            />
                            <label className="absolute bottom-0 right-0 w-6 h-6 bg-white rounded-full border-2 border-white flex items-center justify-center cursor-pointer">
                                <FaCamera className="text-primary text-lg" />
                                <input type="file" accept="image/png, image/jpeg, image/jpg" onChange={handleImageUpload} className="hidden" />
                            </label>
                        </div>
                        <h2 className="text-xl font-semibold mt-2">{user?.firstName} {user?.lastName}</h2>

                        <div className="mt-6 w-full text-sm space-y-3">
                            <div className="flex justify-between">
                                <span>{t('userProfile.stats.skippedReservations')}</span>
                                <span className="text-green-600 font-bold">{status?.skippedCount || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>{t('userProfile.stats.remainingReservations')}</span>
                                <span className="font-bold">{status?.remainingWarnings || 0}</span>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Main Content Card */}
                <motion.div
                    initial={{ opacity: 0, y: 100 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9 }}
                    className="bg-white p-6 rounded-xl shadow-md w-full lg:w-[65%] mx-auto lg:mx-0"
                >
                    <div className="flex flex-wrap border-b border-gray-200 mb-6 space-x-4">
                        {["account", "packages"].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold transition ${
                                    activeTab === tab
                                        ? "border-b-2 border-primary text-primary"
                                        : "text-gray-500 hover:text-primary"
                                }`}
                            >
                                {tab === "account" ? (
                                    <FaUserCog />
                                ) : tab === "packages" ? (
                                    <FaBox />
                                ) : tab === "clinics" ? (
                                    <FaBuilding />
                                ) : (
                                    <FaBell />
                                )}
                                {t(`userProfile.tabs.${tab}`)}
                            </button>
                        ))}
                    </div>

                    {activeTab === "account" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">{t('userProfile.account.firstName')}</label>
                                <p className="border px-3 py-2 rounded bg-gray-50">
                                    {user?.firstName || "-"}
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t('userProfile.account.lastName')}</label>
                                <p className="border px-3 py-2 rounded bg-gray-50">
                                    {user?.lastName || "-"}
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t('userProfile.account.phoneNumber')}</label>
                                <p className="border px-3 py-2 rounded bg-gray-50">
                                    {user?.phoneNumber || "-"}
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t('userProfile.account.email')}</label>
                                <p className="border px-3 py-2 rounded bg-gray-50">
                                    {user?.email || "-"}
                                </p>
                            </div>
                        </div>
                    )}

                    {activeTab === "packages" && (
                        <div>
                            {packagesLoading ? (
                                <div className="text-center py-8">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                    <p className="mt-2 text-gray-500">{t('userProfile.packages.loading')}</p>
                                </div>
                            ) : packages.length === 0 ? (
                                <div className="text-center py-12">
                                    <FaBox className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('userProfile.packages.noPackages')}</h3>
                                    <p className="text-gray-500">{t('userProfile.packages.noPackagesDescription')}</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {packages.map((pkg) => {
                                        const daysRemaining = getDaysRemaining(pkg.expiryDate);
                                        const isExpiringSoon = daysRemaining <= 7 && daysRemaining > 0;
                                        
                                        return (
                                            <motion.div
                                                key={pkg.packageId}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className={`border rounded-lg p-4  text-xs${
                                                    pkg.isExpired 
                                                        ? 'border-red-200 bg-red-50' 
                                                        : isExpiringSoon 
                                                        ? 'border-yellow-200 bg-yellow-50'
                                                        : 'border-gray-200 bg-white'
                                                } hover:shadow-md transition-shadow`}
                                            >
                                                {/* Package Header */}
                                                <div className="flex justify-between items-start mb-3">
                                                    <div>
                                                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                                            {pkg.title}
                                                        </h3>
                                                        <p className="text-gray-600 text-sm">{pkg.description}</p>
                                                    </div>
                                                    <div className="flex items-center text-xs gap-2 ">
                                                        {pkg.isExpired ? (
                                                            <span className="inline-flex  items-center px-2  py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                {/* <FaTimesCircle className="mr-1" /> */}
                                                                {t('userProfile.packages.status.expired')}
                                                            </span>
                                                        ) : isExpiringSoon ? (
                                                            <span className="inline-flex items-center px-3 w-32 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                                <FaClock className="mr-1" />
                                                                {t('userProfile.packages.status.expiringSoon')}
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                <FaCheckCircle className="mr-1" />
                                                                {t('userProfile.packages.status.active')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Package Dates */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                    <div className="flex items-center text-sm text-gray-600">
                                                        <FaCalendarAlt className="mr-2 text-primary" />
                                                        <span>{t('userProfile.packages.purchased')}: {formatDate(pkg.purchaseDate)}</span>
                                                    </div>
                                                    <div className="flex items-center text-sm text-gray-600">
                                                        <FaClock className="mr-2 text-primary" />
                                                        <span>
                                                            {t('userProfile.packages.expires')}: {formatDate(pkg.expiryDate)}
                                                            {!pkg.isExpired && (
                                                                <span className={`ml-1 ${daysRemaining <= 7 ? 'text-red-600' : 'text-gray-500'}`}>
                                                                    ({daysRemaining > 0 ? t('userProfile.packages.daysLeft', { days: daysRemaining }) : t('userProfile.packages.status.expired')})
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Services */}
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-700 mb-2">{t('userProfile.packages.servicesIncluded')}:</h4>
                                                    <div className="space-y-2">
                                                        {pkg.services.map((service) => (
                                                            <div key={service.serviceId} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                                                <span className="text-sm font-medium">{service.serviceName}</span>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs text-gray-500">
                                                                        {t('userProfile.packages.used', { 
                                                                            used: service.usedQuantity, 
                                                                            total: service.totalQuantity 
                                                                        })}
                                                                    </span>
                                                                    <div className="w-16 bg-gray-200 rounded-full h-2">
                                                                        <div 
                                                                            className={`h-2 rounded-full ${
                                                                                service.isFullyUsed ? 'bg-red-500' : 'bg-primary'
                                                                            }`}
                                                                            style={{ 
                                                                                width: `${(service.usedQuantity / service.totalQuantity) * 100}%` 
                                                                            }}
                                                                        ></div>
                                                                    </div>
                                                                    {service.isFullyUsed ? (
                                                                        <FaTimesCircle className="text-red-500 text-sm" />
                                                                    ) : (
                                                                        <FaCheckCircle className="text-green-500 text-sm" />
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default UserProfile;